import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Form, Modal, Space } from 'antd';
import { useDefaultFormErrorNotification, useRouteMatchParam } from '@/hooks';
import {
  CreateWorkOrderObservationRequest,
  UpdateWorkOrderObservationRequest,
  WorkOrderObservation,
} from '@/core/domain/workorder/WorkOrdersObservations/WorkOrdersObservations.types';
import {
  useCreateWorkOrderObservation,
  useUpdateWorkOrderObservation,
} from '@/core/domain/workorder/WorkOrdersObservations/WorkOrdersObservations.hooks';
import { ModalProps } from '@/types/ModalProps';
import WorkOrderObservationForm, {
  WorkOrderObservationData,
} from '../ObservationForm';
import { useEffect } from 'react';
import { compressImage } from '@/utils/functions/compressImage';
import { useArchiveUpload } from '@/core/domain/archive/Archive/Archive.hooks';
import { validateImage } from '@/utils/functions/validateImage';

type Props = {
  observation?: WorkOrderObservation;
} & ModalProps;

function WorkOrderObservationModal({
  visible,
  handleVisible,
  observation,
  ...props
}: Props) {
  const intl = useIntl();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const [form] = Form.useForm<WorkOrderObservationData>();

  const complementId = useRouteMatchParam('complementId');
  const createMutation = useCreateWorkOrderObservation();
  const updateMutation = useUpdateWorkOrderObservation();
  const archiveUpload = useArchiveUpload();

  useEffect(() => {
    if (observation) {
      form.setFieldsValue(observation);
    }
  }, [observation]);

  const handleSubmit = async () => {
    try {
      const { uploadFile, ...values } = await form.validateFields();

      let attachments = values.attachments;
      const uploadedFile = uploadFile?.[0]?.originFileObj as File;

      if (uploadedFile) {
        const isImage = ['image/jpeg', 'image/png'].includes(uploadedFile.type);
        const fileToUpload = isImage
          ? await compressImage(uploadedFile)
          : uploadedFile;

        if (isImage) validateImage(fileToUpload);

        const response = await archiveUpload.mutateAsync({
          file: [fileToUpload],
          isPublic: true,
        });

        attachments = response.filePath;
      }

      const observationData: CreateWorkOrderObservationRequest = {
        ...values,
        workOrderComplementId: +complementId,
        attachments,
      };

      if (observation) {
        const obj: UpdateWorkOrderObservationRequest = {
          ...observationData,
          observationId: observation.id,
          attachments,
        };

        await updateMutation.mutateAsync(obj);
      } else {
        await createMutation.mutateAsync(observationData);
      }

      handleCancel();
    } catch (err) {
      sendFormErrorNotification(err);
    }
  };

  const handleCancel = () => {
    handleVisible();
    form.resetFields();
  };

  const isLoading =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    archiveUpload.isLoading;

  return (
    <Modal
      title={intl.formatMessage({ id: 'workOrders.details.observations' })}
      open={visible}
      onCancel={handleCancel}
      footer={
        <Space>
          <Button onClick={handleCancel}>
            <FormattedMessage id="commons.form.button.close" />
          </Button>
          <Button type="primary" onClick={handleSubmit} loading={isLoading}>
            <FormattedMessage id="commons.entity.save" />
          </Button>
        </Space>
      }
      {...props}
    >
      <WorkOrderObservationForm
        form={form}
        editing={!!observation}
        isWorkOrder
        isLoading={isLoading}
      />
    </Modal>
  );
}

export default WorkOrderObservationModal;
